/**
 * React Query hook for Temporary Funds List V2 with RDS-based pagination
 * Uses the new listTemporaryFundsV2 API with improved filtering and sorting
 */

import { useQuery } from '@tanstack/react-query';
import { listTemporaryFundsV2 } from 'services/Billing/temporaryFunds/temporaryFundServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Fetches temporary funds data with RDS-based pagination
 * API Response format: { total_count, temporary_funds: [], page_count, page, limit }
 *
 * @param {Object} params - Request parameters
 * @returns {Promise<Object>} - Response with data array and pagination metadata
 */
const fetchTemporaryFundsV2 = async ({
  limit = 10,
  page = 1,
  case_id = '',
  account_id = '',
  transaction_date_lower = '',
  transaction_date_upper = '',
  sort_by,
  sort_order,
}) => {
  try {
    const response = await listTemporaryFundsV2({
      limit,
      page,
      case_id,
      account_id,
      transaction_date_lower,
      transaction_date_upper,
      sort_by,
      sort_order,
    });

    if (response?.data) {
      // Transform API response: map 'data' to 'temporary_funds' to match expected structure
      return {
        ...response.data,
        temporary_funds: response.data.data || [],
      };
    }

    // Return empty structure if no data (matching API format)
    return {
      total_count: 0,
      temporary_funds: [],
      page_count: 0,
      page: page,
      limit: limit,
    };
  } catch (error) {
    console.error('Error fetching temporary funds V2:', error);
    throw error;
  }
};

/**
 * Hook to fetch temporary funds with RDS-based pagination using React Query
 *
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Items per page (default: 10)
 * @param {number} options.page - Page number (default: 1)
 * @param {string} options.case_id - Filter by case UUID
 * @param {string} options.account_id - Filter by account UUID (credit or debit)
 * @param {string} options.transaction_date_lower - Start date filter (ISO format)
 * @param {string} options.transaction_date_upper - End date filter (ISO format)
 * @param {string} options.sort_by - Field to sort by (e.g., 'transaction_date', 'case_name', 'amount')
 * @param {string} options.sort_order - Sort order ('asc' or 'desc')
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} Query result object
 * @returns {Array} returns.temporaryFunds - Array of temporary fund transaction objects
 * @returns {number} returns.totalRecords - Total number of records
 * @returns {number} returns.currentPage - Current page number
 * @returns {number} returns.totalPages - Total number of pages
 * @returns {number} returns.pageSize - Items per page
 * @returns {boolean} returns.isLoading - Initial loading state
 * @returns {boolean} returns.isFetching - Fetching state (including background refetch)
 * @returns {boolean} returns.isError - Error state indicator
 * @returns {Object|null} returns.error - Error object if request failed
 * @returns {Function} returns.refetch - Function to manually refetch data
 *
 * @example
 * const {
 *   temporaryFunds,
 *   totalRecords,
 *   totalPages,
 *   isLoading,
 *   refetch
 * } = useTemporaryFundListV2Query({
 *   limit: 10,
 *   page: 1,
 *   case_id: 'case-uuid',
 *   account_id: 'account-uuid',
 *   sort_by: 'transaction_date',
 *   sort_order: 'desc',
 * });
 */
export const useTemporaryFundListV2Query = ({
  limit = 10,
  page = 1,
  case_id = '',
  account_id = '',
  transaction_date_lower = '',
  transaction_date_upper = '',
  sort_by,
  sort_order,
  enabled = true,
} = {}) => {
  // Create stable query key based on all filter parameters
  const queryKey = queryKeys.temporaryFunds.listV2(
    {
      case_id,
      account_id,
      transaction_date_lower,
      transaction_date_upper,
      sort_by,
      sort_order,
    },
    page,
    limit
  );

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchTemporaryFundsV2({
        limit,
        page,
        case_id,
        account_id,
        transaction_date_lower,
        transaction_date_upper,
        sort_by,
        sort_order,
      }),
    enabled,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes - serves from cache before that
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (React Query v4 uses gcTime instead of cacheTime)
    keepPreviousData: true, // Keep previous data while fetching
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: enabled, // Only refetch on mount if query is enabled
    retry: (failureCount) => {
      // Retry twice for errors
      return failureCount < 2;
    },
  });

  return {
    temporaryFunds: data?.temporary_funds || [],
    totalRecords: data?.total_count || 0,
    currentPage: data?.page || page,
    totalPages: data?.page_count || 0,
    pageSize: data?.limit || limit,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

export default useTemporaryFundListV2Query;
